import { HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DOCUMENT, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserChangedEvent, UserService, UserStore } from '@cfp/core';
import { CustomerStylingDTO } from 'csr-login-app';
import { map, mergeMap } from 'rxjs';
import { CustomerStylingService } from './customer-styling-service';

@Component({
  selector: 'cfp-customer-styling',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerStyling {
  private htmlStyleElement?: HTMLStyleElement;

  private readonly userService = inject(UserService);
  private readonly userStore = inject(UserStore);
  private readonly document = inject(DOCUMENT);
  private readonly customerStylingService = inject(CustomerStylingService);

  private readonly tokenChanged = toSignal(
    this.userService.tokenChanged().pipe(mergeMap(() => this.userService.getCustomerStyling())),
    { initialValue: undefined }
  );

  private readonly userChanged = toSignal(
    this.userStore.userChanged.pipe(
      map((event: UserChangedEvent) => {
        if (event.new && event.new.styling) {
          return event.new.styling;
        } else if ((!event.new || !event.new.styling) && this.htmlStyleElement) {
          this.htmlStyleElement.remove();
          this.htmlStyleElement = undefined;
        }
        return undefined;
      })
    ),
    { initialValue: undefined }
  );

  constructor() {
    effect(() => {
      const styling = this.tokenChanged() || this.userChanged();
      if (!styling) {
        this.htmlStyleElement?.remove();
        return;
      }

      if (styling.logoType) {
        this.userService
          .getUserCustomerStylingImage()
          .pipe(
            map((response: HttpResponse<Blob>) => {
              return response.body ? URL.createObjectURL(response.body) : undefined;
            })
          )
          .subscribe((stylingLogoUrl?: string) => {
            this.appendCss(styling, stylingLogoUrl);
          });
      } else {
        this.appendCss(styling);
      }
    });
  }

  private appendCss(customerStyling: CustomerStylingDTO, stylingLogoUrl?: string): void {
    if (this.htmlStyleElement) {
      this.htmlStyleElement.remove();
    }

    const style: HTMLStyleElement = this.document.createElement('style');
    style.innerText = this.customerStylingService.createStyling(customerStyling, stylingLogoUrl);

    this.htmlStyleElement = this.document.head.appendChild(style);
  }
}
